// Verify Application
// -------------------
// Final step of the Add Application wizard — shows the HTTP ownership-
// verification instructions: the web-app meta tag plus, for each configured API
// backend, a nimis-verify endpoint to serve. (DNS verification removed.)
//
// This step only shows instructions and advances the wizard (Finish). The
// actual ownership check runs from the Applications page's Verify action — the
// backend is authoritative; the frontend never performs the check itself.

import { useState, useCallback } from "react";
import { BUTTON_NEXT } from "@/constants/definitions";
import { getVerificationTargets } from "@/utils/verificationTargets";
import { Input } from "@/components/ui/input";

const COPIED_RESET_MS = 2000;
const COPY_KEY_TOKEN = "__token__";

interface VerifyApplicationProps {
  webapp?: { verificationString?: string; url?: string; apiDomains?: unknown };
  onComplete?: (name: string) => void;
}

function VerifyApplication({ webapp, onComplete }: VerifyApplicationProps) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const onButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const target = event.target as HTMLButtonElement;
    if (onComplete) {
      onComplete(target.name);
    }
  }, [onComplete]);

  const copyValue = useCallback(async (key: string, value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), COPIED_RESET_MS);
    } catch {
      // clipboard API unavailable — silent fail; the value is still selectable.
    }
  }, []);

  const verificationString = webapp?.verificationString || "";
  const targets = getVerificationTargets(webapp?.url, webapp?.apiDomains, verificationString);
  const tokenReady = !!verificationString && targets.length > 0;
  const hasApiTargets = targets.some((target) => !target.isPrimary);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <p className="text-sm text-gray-700">
          Verify ownership by adding the meta tag to your web app
          {hasApiTargets ? " and serving a nimis-verify endpoint for each API domain" : ""}.
        </p>
        <p className="text-xs text-gray-400">
          {hasApiTargets
            ? "Everything below must be in place — verification fails until every item is served. "
            : ""}
          All of it must be served over HTTPS — an application served over plain
          HTTP is inherently insecure and isn't tested. We check before the first
          test runs.
        </p>
      </div>

      {!tokenReady ? (
        <p className="text-sm text-red-600">
          Verification token unavailable — please go back and try again.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-600">Verification token</span>
            <div className="flex items-center gap-2">
              <Input type="text" readOnly value={verificationString} className="flex-1 text-xs bg-gray-50" />
              <button
                type="button"
                onClick={() => copyValue(COPY_KEY_TOKEN, verificationString)}
                className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer shrink-0"
              >
                {copiedKey === COPY_KEY_TOKEN ? "Copied" : "Copy token"}
              </button>
            </div>
          </div>

          {targets.map((target) => (
            <div key={target.label} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600 break-all">{target.label}</span>
                <span className="text-[10px] uppercase tracking-wider text-gray-400">
                  {target.isPrimary ? "Web App" : "API"}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {target.isPrimary
                  ? "Add this tag to the page at your web-app URL:"
                  : "Serve this endpoint returning the token above as text/plain:"}
              </p>
              <div className="flex items-center gap-2">
                <Input type="text" readOnly value={target.copyValue} className="flex-1 text-xs bg-gray-50" />
                <button
                  type="button"
                  onClick={() => copyValue(target.label, target.copyValue)}
                  className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer shrink-0"
                >
                  {copiedKey === target.label ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          ))}

          {hasApiTargets && (
            <p className="text-xs text-gray-400">
              Only add API domains hosted on a different host or subdomain than your web app —
              same-host APIs are already covered by the web-app verification.
            </p>
          )}
        </div>
      )}

      {/* Previous is intentionally not surfaced here. By this step the
          BE record exists; routing the user back to edit form values that
          have already been persisted invites divergence between the
          in-memory state and the stored record. */}
      <div className="flex items-center justify-end pt-4 border-t border-gray-100">
        <button
          type="button"
          name={BUTTON_NEXT}
          onClick={onButtonClick}
          className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 cursor-pointer"
        >
          Finish
        </button>
      </div>
    </div>
  );
}

export default VerifyApplication;
